import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionLabel } from '@/components/ui/section-label';
import { cn } from '@/lib/utils';
import {
  assumptionDimensions,
  deriveRiskLevel,
  isImpossibleBlocker,
  withDimensions,
  type Certainty,
  type Importance,
  type RiskLevel,
} from '@/lib/assumption-risk';
import type { NodeMetadata } from '@/services/canvas-mappers';
import type { Node as ReactFlowNode } from '@xyflow/react';
import type { StreamedAssumption } from '@/types/api';
import { toast } from '@/hooks/use-toast';
import { AssumptionDetail } from './AssumptionDetail';
import { AssumptionSticky } from './AssumptionSticky';
import { useAssumptionMutations } from './use-assumption-mutations';

// The 2×2: importance up the side, certainty across the top, so the quadrant
// that needs work (important and untested) lands in the top-right corner. Cells
// are the only placement there is — no free coordinates, nothing persisted about
// layout. A sticky's cell IS its classification.
interface Cell {
  importance: Importance;
  certainty: Certainty;
  caption: string;
}

const CELLS: Cell[] = [
  { importance: 'high', certainty: 'known', caption: 'Well supported' },
  { importance: 'high', certainty: 'unknown', caption: 'Test first' },
  { importance: 'low', certainty: 'known', caption: 'Low stakes' },
  { importance: 'low', certainty: 'unknown', caption: 'Keep an eye on it' },
];

const cellKey = (importance: Importance, certainty: Certainty) => `${importance}:${certainty}`;

// Built once at module scope so a caption lookup never needs a non-null
// assertion — every (importance, certainty) pair is a key CELLS actually has.
const CELLS_BY_KEY = new Map<string, Cell>(CELLS.map((c) => [cellKey(c.importance, c.certainty), c]));

// A sticky the PM just moved out of the critical quadrant. Leaving critical
// removes a tension from step 7, so the board offers (never requires) a recorded
// why. The move itself is already saved by the time this appears — the note is a
// second, optional patch. It carries the exact importance/certainty/riskLevel
// the move persisted (not whatever a later read of the node happens to return),
// so that second patch can re-assert them instead of trusting a possibly stale
// read.
interface PendingNote {
  nodeId: string;
  importance: Importance;
  certainty: Certainty;
  riskLevel: RiskLevel;
}

export function AssumptionBoard({
  projectId,
  assumptions,
  canEdit,
  streamed,
  draftingTitle,
}: {
  projectId: string;
  assumptions: ReactFlowNode[];
  canEdit: boolean;
  streamed?: StreamedAssumption[];
  draftingTitle?: string | null;
}) {
  const { patchMeta, deleteAssumption, busyId } = useAssumptionMutations(projectId);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overCell, setOverCell] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailNode = detailId ? assumptions.find((n) => n.id === detailId) : undefined;
  // Stable across board re-renders (setDetailId from useState never changes
  // identity), unlike an inline arrow passed as onClose, which would give the
  // panel's document-level Escape listener a new dependency every render and
  // force it to re-subscribe constantly.
  const closeDetail = useCallback(() => setDetailId(null), []);
  // Dimensions the server hasn't confirmed yet. Without this the sticky snaps
  // back to its old cell for the length of the refetch, which reads as a failed
  // drag.
  const [pendingDims, setPendingDims] = useState<Record<string, { importance: Importance; certainty: Certainty }>>({});
  const [note, setNote] = useState<PendingNote | null>(null);

  const metaOf = useCallback(
    (node: ReactFlowNode) => (node.data as { metadata?: NodeMetadata } | undefined)?.metadata,
    [],
  );

  const reclassify = useCallback(
    async (nodeId: string, importance: Importance, certainty: Certainty) => {
      const node = assumptions.find((n) => n.id === nodeId);
      if (!node || busyId) return;
      const meta = metaOf(node);
      const current = pendingDims[nodeId] ?? assumptionDimensions(meta);
      if (current.importance === importance && current.certainty === certainty) return;

      const newLevel = deriveRiskLevel(importance, certainty);
      const leavingCritical =
        deriveRiskLevel(current.importance, current.certainty) === 'critical' && newLevel !== 'critical';

      setPendingDims((prev) => ({ ...prev, [nodeId]: { importance, certainty } }));
      // Spread rather than pass `meta` straight through: NodeMetadata is a
      // named interface (no index signature), so it isn't structurally
      // assignable to withDimensions' `Record<string, unknown>` constraint,
      // while a fresh object literal is.
      const persisted = withDimensions({ ...meta }, importance, certainty);
      const ok = await patchMeta(nodeId, persisted, 'Move failed');
      setPendingDims((prev) => {
        const next = { ...prev };
        delete next[nodeId];
        return next;
      });
      if (ok && leavingCritical) {
        setNote({
          nodeId,
          importance: persisted.importance,
          certainty: persisted.certainty,
          riskLevel: persisted.riskLevel,
        });
      } else if (ok && newLevel === 'critical') {
        // Moving back into critical: a pending note for this same assumption
        // is now stale (the panel hides downgradeReason while critical), so
        // don't let it linger to be saved out of context later. A note open
        // for some other assumption is unrelated and stays.
        setNote((prev) => (prev && prev.nodeId === nodeId ? null : prev));
      }
    },
    [assumptions, busyId, metaOf, patchMeta, pendingDims],
  );

  const saveNote = useCallback(
    async (text: string) => {
      // Mirror reclassify's guard: without this, the note could still be saved
      // while another mutation (a drag, a dismiss) is already writing to the graph.
      if (!note || busyId) return;
      const trimmed = text.trim();
      if (!trimmed) {
        setNote(null);
        return;
      }
      const node = assumptions.find((n) => n.id === note.nodeId);
      if (!node) {
        // Mirror reclassify's guard: without a live node, spreading its
        // metadata degrades to `{}`, so the PUT below would wipe importance,
        // certainty, risk level and every AI flag in one shot.
        setNote(null);
        toast({
          title: 'Note not saved',
          description: 'That assumption is no longer there.',
          variant: 'destructive',
        });
        return;
      }
      // The node list is read without ConsistentRead, so this fresh read can
      // still return the metadata from before the move landed. Start from it
      // anyway so a concurrent editor's own change (a dismiss, a restore)
      // survives, then overlay the importance/certainty/riskLevel the move
      // itself persisted, so a stale read here can't quietly revert the
      // reclassification this note exists to explain.
      const ok = await patchMeta(
        note.nodeId,
        {
          ...(metaOf(node) ?? {}),
          importance: note.importance,
          certainty: note.certainty,
          riskLevel: note.riskLevel,
          downgradeReason: trimmed,
        },
        'Note not saved',
      );
      if (ok) setNote(null);
    },
    [assumptions, note, metaOf, patchMeta, busyId],
  );

  // An active impossible flag isn't a risk level, it's a dead end — it has no
  // cell to sit in, so it's pulled out here before anything reaches byCell.
  // Dismissing the flag (or restoring it) is a metadata patch like any other
  // reclassification: the canvas refetch it triggers recomputes this on the
  // next render, so a blocker's move on/off the board needs no extra wiring.
  const blockers = useMemo(() => assumptions.filter((node) => isImpossibleBlocker(metaOf(node))), [assumptions, metaOf]);
  const boardAssumptions = useMemo(
    () => assumptions.filter((node) => !isImpossibleBlocker(metaOf(node))),
    [assumptions, metaOf],
  );

  const byCell = useMemo(() => {
    const out = new Map<string, ReactFlowNode[]>(CELLS.map((c) => [cellKey(c.importance, c.certainty), []]));
    for (const node of boardAssumptions) {
      const dims = pendingDims[node.id] ?? assumptionDimensions(metaOf(node));
      out.get(cellKey(dims.importance, dims.certainty))?.push(node);
    }
    return out;
  }, [boardAssumptions, pendingDims, metaOf]);

  // Previews are the model's raw proposals: the server normalises before
  // persisting and may drop some, so these are render-only and un-draggable.
  // They vanish when the run's refetch replaces them with real stickies. A
  // preview flagged impossible goes to the band, same as a saved one, rather
  // than sitting in a cell for the length of the run.
  const streamedByCell = useMemo(() => {
    const out = new Map<string, StreamedAssumption[]>(CELLS.map((c) => [cellKey(c.importance, c.certainty), []]));
    for (const item of streamed ?? []) {
      if (!item.title || item.impossible) continue;
      const importance = item.importance === 'high' || item.importance === 'low' ? item.importance : 'low';
      const certainty = item.certainty === 'known' || item.certainty === 'unknown' ? item.certainty : 'unknown';
      out.get(cellKey(importance, certainty))?.push(item);
    }
    return out;
  }, [streamed]);

  const streamedBlockers = useMemo(
    () => (streamed ?? []).filter((item) => item.title && item.impossible),
    [streamed],
  );

  // The note has exactly one home. When the panel is already open for the same
  // assumption the move just changed, the panel owns it — an in-cell prompt
  // would render behind the panel's overlay. Otherwise the destination cell
  // owns it, including while the panel is open for a *different* assumption:
  // the note persists until saved or dismissed, so closing that panel reveals
  // it. Decided once here, not by the panel or the cells independently.
  const noteInPanel = note !== null && detailId === note.nodeId;
  // One element, built once regardless of which slot below ends up rendering
  // it. `key`'d on the assumption so switching to a different one remounts a
  // clean draft instead of carrying stale text over.
  const notePrompt: ReactNode = note ? (
    <DowngradePrompt
      key={note.nodeId}
      busy={busyId !== null}
      onSave={saveNote}
      onDismiss={() => setNote(null)}
    />
  ) : null;

  return (
    <>
      {(blockers.length > 0 || streamedBlockers.length > 0) && (
        <BlockerBand blockers={blockers} streamed={streamedBlockers} onOpen={setDetailId} />
      )}
      <div className="rounded-lg border bg-card p-4">
        <div className="grid grid-cols-[6rem_1fr_1fr] gap-2">
          <div />
          <SectionLabel className="pb-1 text-center">We know</SectionLabel>
          <SectionLabel className="pb-1 text-center">We don&rsquo;t know</SectionLabel>

          {(['high', 'low'] as Importance[]).map((importance) =>
            (['known', 'unknown'] as Certainty[]).map((certainty, i) => {
              // The one nodeId this cell's prompt (if any) is about — matched
              // against the note's own stashed importance/certainty (what the
              // move persisted), never a fresh node lookup, since a node-list
              // read can still lag behind the move that created this note
              // (see saveNote's payload layering). `prompt` derives from this
              // same value rather than re-testing the condition, so the ring
              // BoardCell draws from it can never point at a different
              // assumption than the prompt itself.
              const promptNodeId =
                !noteInPanel && note && note.importance === importance && note.certainty === certainty
                  ? note.nodeId
                  : null;
              return (
                <BoardCell
                  key={cellKey(importance, certainty)}
                  importance={importance}
                  certainty={certainty}
                  rowLabel={i === 0 ? (importance === 'high' ? 'Important' : 'Less important') : undefined}
                  nodes={byCell.get(cellKey(importance, certainty)) ?? []}
                  streamed={streamedByCell.get(cellKey(importance, certainty)) ?? []}
                  drafting={
                    draftingTitle && importance === 'low' && certainty === 'unknown' ? draftingTitle : null
                  }
                  canEdit={canEdit}
                  busyId={busyId}
                  draggingId={draggingId}
                  isOver={overCell === cellKey(importance, certainty)}
                  onDragStart={setDraggingId}
                  onDragEnd={() => {
                    setDraggingId(null);
                    setOverCell(null);
                  }}
                  onCellEnter={() => setOverCell(cellKey(importance, certainty))}
                  onCellLeave={() => setOverCell((prev) => (prev === cellKey(importance, certainty) ? null : prev))}
                  onDropSticky={(nodeId) => {
                    setDraggingId(null);
                    setOverCell(null);
                    void reclassify(nodeId, importance, certainty);
                  }}
                  onOpen={setDetailId}
                  prompt={promptNodeId ? notePrompt : null}
                  promptNodeId={promptNodeId}
                />
              );
            }),
          )}
        </div>
      </div>
      {detailNode && (
        // `contents` (display: contents) makes this div invisible to layout —
        // it generates no box, so any margin a *further-out* ancestor's
        // space-y-* utility puts on it (this board renders inside one) is
        // simply dropped rather than passed through. Without it, this fixed
        // inset-0 panel would still be a direct DOM child of that ancestor
        // (fragments don't create a DOM node of their own), so the same
        // over-constrained top+bottom+margin math this fix is about would
        // reapply one level up, just with a different margin value.
        <div className="contents">
          <AssumptionDetail
            node={detailNode}
            canEdit={canEdit}
            busy={busyId !== null}
            onClose={closeDetail}
            onReclassify={(nodeId, importance, certainty) => void reclassify(nodeId, importance, certainty)}
            onPatchMeta={patchMeta}
            onDelete={deleteAssumption}
            prompt={noteInPanel ? notePrompt : null}
          />
        </div>
      )}
    </>
  );
}

// One board cell: an optional row label (rendered as a grid sibling so it lines
// up beside its row's two cells) plus the drop target itself.
function BoardCell({
  importance,
  certainty,
  rowLabel,
  nodes,
  streamed,
  drafting,
  canEdit,
  busyId,
  draggingId,
  isOver,
  onDragStart,
  onDragEnd,
  onCellEnter,
  onCellLeave,
  onDropSticky,
  onOpen,
  prompt,
  promptNodeId,
}: {
  importance: Importance;
  certainty: Certainty;
  rowLabel?: string;
  nodes: ReactFlowNode[];
  streamed: StreamedAssumption[];
  drafting: string | null;
  canEdit: boolean;
  busyId: string | null;
  draggingId: string | null;
  isOver: boolean;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onCellEnter: () => void;
  onCellLeave: () => void;
  onDropSticky: (nodeId: string) => void;
  onOpen: (id: string) => void;
  prompt: ReactNode;
  // The assumption this cell's open prompt is about, if any — ringed on the
  // matching sticky so the pair reads as connected and a silent subject
  // switch (moving a second assumption into the same cell while the prompt
  // is still open for the first) becomes visible instead.
  promptNodeId: string | null;
}) {
  const cell = CELLS_BY_KEY.get(cellKey(importance, certainty));
  const risk = deriveRiskLevel(importance, certainty);
  // Exactly one background and one border-style/colour pair ever apply: pick
  // the cell's visual state up front (drag-over first, since dragging is an
  // action and should always read as pine, then the derived risk tier) and
  // emit a single string for it. Layering conditional classes instead would
  // put two same-property arbitrary-value utilities in the class list at
  // once, and Tailwind's stylesheet emission order — not this component —
  // would end up deciding which one paints.
  const boxTone = isOver
    ? 'border-solid border-primary/50 bg-primary/6'
    : risk === 'critical'
      ? 'border-solid border-destructive/60 bg-destructive/[0.07]'
      : risk === 'risky'
        ? 'border-solid border-amber-500/45 bg-amber-500/10'
        : 'border-dashed border-border bg-muted/20';
  // The caption text already carries the meaning (see step 5 of the brief:
  // no aria for the colour), so only the critical tier additionally tints
  // its caption; risky's promotion is the count alone, which keeps it a
  // visible notch below critical rather than matching it.
  const captionTone = risk === 'critical' ? 'text-destructive' : 'text-muted-foreground';
  const countTone =
    risk === 'critical'
      ? 'text-base font-semibold tabular-nums text-destructive'
      : risk === 'risky'
        ? 'text-[11px] font-medium tabular-nums text-amber-700'
        : 'text-[11px] tabular-nums text-muted-foreground';
  return (
    <>
      {rowLabel && (
        <SectionLabel className="flex items-center justify-end pr-1 text-right">{rowLabel}</SectionLabel>
      )}
      <div
        onDragOver={(e) => {
          if (!canEdit || !draggingId) return;
          // preventDefault is what marks this element as a valid drop target.
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          onCellEnter();
        }}
        onDragLeave={onCellLeave}
        onDrop={(e) => {
          if (!canEdit) return;
          e.preventDefault();
          const nodeId = e.dataTransfer.getData('text/plain');
          if (nodeId) onDropSticky(nodeId);
        }}
        className={cn('min-h-38 rounded-md border p-2 transition-colors', boxTone)}
      >
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <span className={cn('text-[11px] font-medium', captionTone)}>{cell?.caption}</span>
          <span className={countTone}>{nodes.length || ''}</span>
        </div>
        {prompt}
        <div className="flex flex-wrap gap-2">
          {nodes.map((node) => (
            <AssumptionSticky
              key={node.id}
              node={node}
              onOpen={onOpen}
              // Not draggable mid-mutation: reclassify bails for any node while
              // busyId is set, and a draggable sticky that silently no-ops on
              // drop reads as a broken drag rather than a busy board.
              draggable={canEdit && !busyId}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              dimmed={draggingId === node.id}
              ringed={promptNodeId === node.id}
            />
          ))}
          {/* Indexed key is safe only because streamed items are append-only and a
              completed item's cell never changes once assigned (see
              use-stream-preview.ts / partial-json.ts) — otherwise index would be
              the wrong identity for a card that can move between renders. */}
          {streamed.map((item, i) => (
            <div
              key={`streamed-${i}`}
              className="w-34 animate-in fade-in rounded-md border border-dashed bg-muted/40 p-2 text-xs leading-snug opacity-80"
            >
              <span className="line-clamp-4 font-medium">{item.title}</span>
              {item.impossible && (
                <AlertTriangle className="mt-1 h-3 w-3 text-destructive" aria-label="Flagged impossible" />
              )}
            </div>
          ))}
          {drafting && (
            <div className="w-34 animate-pulse rounded-md border border-dashed bg-muted/30 p-2 text-xs leading-snug opacity-70">
              <span className="line-clamp-4">{drafting}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// A hard stop lives above the board, not inside a cell: `impossible` isn't a
// risk level, so there's no cell that wouldn't misrepresent it (see the
// module-level note on the 2×2's two axes). Shown once, whether the assumption
// is saved or still a streaming preview.
function BlockerBand({
  blockers,
  streamed,
  onOpen,
}: {
  blockers: ReactFlowNode[];
  streamed: StreamedAssumption[];
  onOpen: (id: string) => void;
}) {
  return (
    // border-destructive/70, stronger than the critical cell's /60: a hard
    // stop is a dead end, not a "test this next" prompt, and now that the
    // critical cell carries a real wash this band needs to visibly outrank
    // it rather than tie with it.
    <div className="rounded-lg border border-destructive/70 bg-card p-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <SectionLabel>Hard stops</SectionLabel>
        <span className="text-[11px] tabular-nums text-muted-foreground">{blockers.length || ''}</span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        These look impossible. Open one to dismiss the flag if you disagree.
      </p>
      <div className="flex flex-wrap gap-2">
        {blockers.map((node) => (
          <AssumptionSticky
            key={node.id}
            node={node}
            onOpen={onOpen}
            // No cell to drag from, so there's nothing dragging one out of the
            // band would mean.
            draggable={false}
            onDragStart={() => {}}
            onDragEnd={() => {}}
            blocker
          />
        ))}
        {streamed.map((item, i) => (
          <div
            key={`streamed-blocker-${i}`}
            className="w-34 animate-in fade-in rounded-md border border-dashed bg-muted/40 p-2 text-xs leading-snug opacity-80"
          >
            <span className="line-clamp-4 font-medium">{item.title}</span>
            <AlertTriangle className="mt-1 h-3 w-3 text-destructive" aria-label="Flagged impossible" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Non-blocking: the move is already saved, and skipping this costs nothing.
// Renders inline wherever AssumptionBoard decides it belongs (a cell or the
// detail panel — see the placement decision above), so it needs its own
// raised surface to read as attached-to-that-change rather than as more of
// whatever background it's sitting on: a cell can be tinted by risk, and the
// panel body is flat, so an opaque bg-card plus a border and shadow has to
// carry that distinction in both cases.
function DowngradePrompt({
  busy,
  onSave,
  onDismiss,
}: {
  busy: boolean;
  onSave: (text: string) => void;
  onDismiss: () => void;
}) {
  const [text, setText] = useState('');
  return (
    // Single wrapping row, not a fixed one-line layout: this renders at the
    // cell's ~530px and the detail panel's max-w-md, so it has to fold to two
    // lines gracefully at the narrower width rather than being tuned to either.
    <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2 shadow-xs">
      <p className="text-xs text-muted-foreground">
        No longer critical, so it drops off your pivot or persevere review.
      </p>
      <input
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 500))}
        placeholder="Why is it less risky now?"
        // Same wording as the placeholder, because a placeholder is not a
        // reliable accessible name: it is the last fallback in the name
        // calculation and it disappears the moment you start typing, so a
        // screen reader user can lose what the field was for mid-sentence.
        aria-label="Why is it less risky now?"
        // The sticky (or the panel's own heading) already names the
        // assumption, so the placeholder doesn't have to — a genuine
        // minimum width is enough to keep it from clipping, and it's also
        // what forces the row to wrap instead of squeezing the input.
        className="min-w-48 flex-1 rounded-md border bg-background px-2 py-1 text-sm focus:outline-hidden focus:ring-1 focus:ring-ring"
        onKeyDown={(e) => {
          // Inert while a mutation is in flight, same as the Save button
          // below: saving here can race a stale read against another write.
          if (e.key === 'Enter' && !busy) onSave(text);
          // Stop propagation so this only cancels the prompt. Left
          // unstopped, the same keypress would also reach the panel's
          // document-level Escape listener and close the whole dialog.
          if (e.key === 'Escape') {
            e.stopPropagation();
            onDismiss();
          }
        }}
      />
      {/* Outline, not the default solid pine: the step already has one brand
          CTA ("Generate more"), and this prompt is loud enough on its own
          without a second button competing for the same attention. */}
      <Button variant="outline" size="sm" onClick={() => onSave(text)} disabled={busy}>
        Save
      </Button>
      {/* Dismissing isn't a write, so it stays clickable even mid-mutation. */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 text-muted-foreground"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
